import { View, Text, styled } from 'tamagui'
import { useState, useMemo } from 'react'
import { Copy, Check } from '@tamagui/lucide-icons'
import { LinkPreview } from './LinkPreview'

const MonoText = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 14,
  lineHeight: 22,
  color: '$surfaceForeground',
})

const BoldText = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 14,
  lineHeight: 22,
  fontWeight: '700',
  color: '$surfaceForeground',
})

const ItalicText = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 14,
  lineHeight: 22,
  fontStyle: 'italic',
  color: '$surfaceForeground',
})

const InlineCode = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 13,
  lineHeight: 22,
  color: '$accent',
  backgroundColor: '$sidebar',
  borderRadius: '$borderRadiusSm',
  paddingHorizontal: 4,
  paddingVertical: 1,
})

const CodeBlock = styled(View, {
  backgroundColor: '$sidebar',
  borderRadius: '$borderRadiusSm',
  paddingHorizontal: 12,
  paddingVertical: 8,
  marginVertical: 4,
  borderWidth: 1,
  borderColor: '$borderDefault',
  position: 'relative',
})

const CodeBlockText = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 13,
  lineHeight: 20,
  color: '$surfaceForeground',
})

const CodeBlockHeader = styled(View, {
  position: 'absolute' as any,
  top: 6,
  right: 8,
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
  zIndex: 1,
})

const LangLabel = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 11,
  color: '$color8',
  textTransform: 'lowercase',
})

// Simple syntax highlighting — no external library
const JS_KEYWORDS = new Set([
  'function', 'const', 'let', 'var', 'async', 'await', 'return', 'if', 'else',
  'import', 'from', 'export', 'type', 'interface', 'class', 'new', 'throw',
  'try', 'catch', 'finally', 'for', 'while', 'do', 'switch', 'case', 'break',
  'continue', 'default', 'true', 'false', 'null', 'undefined', 'typeof', 'void',
])

function highlightCode(code: string, lang: string): React.ReactNode[] {
  if (!lang || !['js', 'ts', 'javascript', 'typescript', 'jsx', 'tsx'].includes(lang)) {
    return code.split('\n').map((line, i) => (
      <Text key={i} fontFamily="$fontMono" fontSize={13} lineHeight={20} color="$surfaceForeground" style={{ display: 'block' } as any}>
        {line || '\n'}
      </Text>
    ))
  }

  return code.split('\n').map((line, lineIdx) => {
    const tokens: React.ReactNode[] = []
    // Tokenize: comments, strings, numbers, keywords, rest
    const tokenRegex = /(\/\/.*$)|('(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*"|`(?:[^`\\]|\\.)*`)|(\b\d+(?:\.\d+)?\b)|(\b[a-zA-Z_$]\w*\b)/gm
    let lastIndex = 0
    let match: RegExpExecArray | null
    let tokenIdx = 0

    while ((match = tokenRegex.exec(line)) !== null) {
      if (match.index > lastIndex) {
        tokens.push(
          <Text key={`${lineIdx}-${tokenIdx++}`} fontFamily="$fontMono" fontSize={13} lineHeight={20} color="$surfaceForeground">
            {line.slice(lastIndex, match.index)}
          </Text>
        )
      }

      if (match[1]) {
        // Comment
        tokens.push(<Text key={`${lineIdx}-${tokenIdx++}`} fontFamily="$fontMono" fontSize={13} lineHeight={20} color="$color8">{match[1]}</Text>)
      } else if (match[2]) {
        // String
        tokens.push(<Text key={`${lineIdx}-${tokenIdx++}`} fontFamily="$fontMono" fontSize={13} lineHeight={20} color="#3ECF7B">{match[2]}</Text>)
      } else if (match[3]) {
        // Number
        tokens.push(<Text key={`${lineIdx}-${tokenIdx++}`} fontFamily="$fontMono" fontSize={13} lineHeight={20} color="#E8A840">{match[3]}</Text>)
      } else if (match[4]) {
        // Identifier — check if keyword
        const word = match[4]
        if (JS_KEYWORDS.has(word)) {
          tokens.push(<Text key={`${lineIdx}-${tokenIdx++}`} fontFamily="$fontMono" fontSize={13} lineHeight={20} color="#3ECFCF">{word}</Text>)
        } else {
          tokens.push(<Text key={`${lineIdx}-${tokenIdx++}`} fontFamily="$fontMono" fontSize={13} lineHeight={20} color="$surfaceForeground">{word}</Text>)
        }
      }
      lastIndex = match.index + match[0].length
    }

    if (lastIndex < line.length) {
      tokens.push(
        <Text key={`${lineIdx}-${tokenIdx++}`} fontFamily="$fontMono" fontSize={13} lineHeight={20} color="$surfaceForeground">
          {line.slice(lastIndex)}
        </Text>
      )
    }

    return (
      <Text key={lineIdx} fontFamily="$fontMono" fontSize={13} lineHeight={20} style={{ display: 'block' } as any}>
        {tokens.length > 0 ? tokens : '\n'}
      </Text>
    )
  })
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <View
      width={24}
      height={24}
      borderRadius="$borderRadiusSm"
      alignItems="center"
      justifyContent="center"
      cursor="pointer"
      opacity={0.6}
      hoverStyle={{ opacity: 1, backgroundColor: '$sidebarHover' }}
      onPress={handleCopy}
      // @ts-expect-error -- web transition
      style={{ transition: 'opacity 150ms ease' }}
    >
      {copied ? <Check size={14} color="#3ECF7B" /> : <Copy size={14} color="$sidebarMuted" />}
    </View>
  )
}

const SpoilerText = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 14,
  lineHeight: 22,
  backgroundColor: '$sidebarMuted',
  color: 'transparent',
  borderRadius: '$borderRadiusSm',
  paddingHorizontal: 2,
  cursor: 'pointer',
  variants: {
    revealed: {
      true: {
        backgroundColor: '$sidebar',
        color: '$surfaceForeground',
      },
    },
  } as const,
})

const BlockquoteContainer = styled(View, {
  borderLeftWidth: 3,
  borderLeftColor: '$accent',
  paddingLeft: 12,
  marginVertical: 4,
})

const BlockquoteText = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 14,
  lineHeight: 22,
  color: '$sidebarMuted',
})

const BulletItem = styled(View, {
  flexDirection: 'row',
  gap: 8,
  paddingLeft: 4,
})

const BulletDot = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 14,
  lineHeight: 22,
  color: '$sidebarMuted',
})

const LinkText = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 14,
  lineHeight: 22,
  color: '$accent',
  cursor: 'pointer',
  hoverStyle: {
    textDecorationLine: 'underline',
  },
})

interface SpoilerProps {
  text: string
}

function Spoiler({ text }: SpoilerProps) {
  const [revealed, setRevealed] = useState(false)
  return (
    <SpoilerText revealed={revealed} onPress={() => setRevealed(!revealed)}>
      {text}
    </SpoilerText>
  )
}

// URL regex
const URL_REGEX = /https?:\/\/[^\s<>)"']+/g

type InlineNode =
  | { type: 'text'; content: string }
  | { type: 'bold'; content: string }
  | { type: 'italic'; content: string }
  | { type: 'code'; content: string }
  | { type: 'spoiler'; content: string }
  | { type: 'link'; url: string }

function parseInline(text: string): InlineNode[] {
  const nodes: InlineNode[] = []
  // Combined regex for inline patterns
  const inlineRegex = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|(`([^`]+?)`)|(\|\|(.+?)\|\|)|(https?:\/\/[^\s<>)"']+)/g

  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = inlineRegex.exec(text)) !== null) {
    // Push preceding text
    if (match.index > lastIndex) {
      nodes.push({ type: 'text', content: text.slice(lastIndex, match.index) })
    }

    if (match[1]) {
      // **bold**
      nodes.push({ type: 'bold', content: match[2] })
    } else if (match[3]) {
      // *italic*
      nodes.push({ type: 'italic', content: match[4] })
    } else if (match[5]) {
      // `code`
      nodes.push({ type: 'code', content: match[6] })
    } else if (match[7]) {
      // ||spoiler||
      nodes.push({ type: 'spoiler', content: match[8] })
    } else if (match[9]) {
      // URL
      nodes.push({ type: 'link', url: match[9] })
    }

    lastIndex = match.index + match[0].length
  }

  // Remaining text
  if (lastIndex < text.length) {
    nodes.push({ type: 'text', content: text.slice(lastIndex) })
  }

  return nodes
}

function renderInline(nodes: InlineNode[], keyPrefix: string) {
  return nodes.map((node, i) => {
    const key = `${keyPrefix}-${i}`
    switch (node.type) {
      case 'text':
        return <MonoText key={key}>{node.content}</MonoText>
      case 'bold':
        return <BoldText key={key}>{node.content}</BoldText>
      case 'italic':
        return <ItalicText key={key}>{node.content}</ItalicText>
      case 'code':
        return <InlineCode key={key}>{node.content}</InlineCode>
      case 'spoiler':
        return <Spoiler key={key} text={node.content} />
      case 'link':
        return (
          <LinkText
            key={key}
            onPress={() => window.open(node.url, '_blank', 'noopener,noreferrer')}
          >
            {node.url}
          </LinkText>
        )
      default:
        return null
    }
  })
}

interface MessageContentProps {
  content: string
}

export function MessageContent({ content }: MessageContentProps) {
  const { elements, urls } = useMemo(() => {
    const lines = content.split('\n')
    const result: React.ReactNode[] = []
    const detectedUrls: string[] = []
    let inCodeBlock = false
    let codeBlockContent: string[] = []
    let codeBlockLang = ''
    let blockIndex = 0

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]

      // Code block toggle
      if (line.trimStart().startsWith('```')) {
        if (inCodeBlock) {
          // End code block
          const codeText = codeBlockContent.join('\n')
          result.push(
            <CodeBlock key={`cb-${blockIndex}`}>
              <CodeBlockHeader>
                {codeBlockLang && <LangLabel>{codeBlockLang}</LangLabel>}
                <CopyButton text={codeText} />
              </CodeBlockHeader>
              {codeBlockLang ? highlightCode(codeText, codeBlockLang) : <CodeBlockText>{codeText}</CodeBlockText>}
            </CodeBlock>
          )
          codeBlockContent = []
          codeBlockLang = ''
          inCodeBlock = false
          blockIndex++
          continue
        } else {
          inCodeBlock = true
          codeBlockLang = line.trimStart().slice(3).trim()
          continue
        }
      }

      if (inCodeBlock) {
        codeBlockContent.push(line)
        continue
      }

      // Blockquote
      if (line.startsWith('> ')) {
        const quoteText = line.slice(2)
        result.push(
          <BlockquoteContainer key={`bq-${i}`}>
            <BlockquoteText>{quoteText}</BlockquoteText>
          </BlockquoteContainer>
        )
        continue
      }

      // Headings
      if (/^### /.test(line)) {
        const headingText = line.slice(4)
        const inlineNodes = parseInline(headingText)
        result.push(
          <Text key={`h3-${i}`} fontFamily="$fontHeading" fontSize={16} fontWeight="700" lineHeight={24} color="$surfaceForeground" style={{ display: 'block', marginTop: 8, marginBottom: 4 } as any}>
            {renderInline(inlineNodes, `h3-${i}`)}
          </Text>
        )
        continue
      }
      if (/^## /.test(line)) {
        const headingText = line.slice(3)
        const inlineNodes = parseInline(headingText)
        result.push(
          <Text key={`h2-${i}`} fontFamily="$fontHeading" fontSize={20} fontWeight="700" lineHeight={28} color="$surfaceForeground" style={{ display: 'block', marginTop: 12, marginBottom: 4 } as any}>
            {renderInline(inlineNodes, `h2-${i}`)}
          </Text>
        )
        continue
      }
      if (/^# /.test(line)) {
        const headingText = line.slice(2)
        const inlineNodes = parseInline(headingText)
        result.push(
          <Text key={`h1-${i}`} fontFamily="$fontHeading" fontSize={24} fontWeight="700" lineHeight={32} color="$surfaceForeground" style={{ display: 'block', marginTop: 16, marginBottom: 4 } as any}>
            {renderInline(inlineNodes, `h1-${i}`)}
          </Text>
        )
        continue
      }

      // Horizontal rule
      if (/^(---|___|[*]{3,})$/.test(line.trim())) {
        result.push(
          <View key={`hr-${i}`} height={1} backgroundColor="$borderDefault" marginVertical={8} />
        )
        continue
      }

      // Numbered list
      const numberedMatch = line.match(/^(\d+)\. (.+)$/)
      if (numberedMatch) {
        const num = numberedMatch[1]
        const itemText = numberedMatch[2]
        const inlineNodes = parseInline(itemText)
        result.push(
          <BulletItem key={`ol-${i}`}>
            <Text fontFamily="$fontMono" fontSize={14} lineHeight={22} color="$sidebarMuted" style={{ minWidth: 20 } as any}>{num}.</Text>
            <Text style={{ flexDirection: 'row', flexWrap: 'wrap', flex: 1 }}>
              {renderInline(inlineNodes, `ol-${i}`)}
            </Text>
          </BulletItem>
        )
        continue
      }

      // Bullet list
      if (/^[-*] /.test(line)) {
        const itemText = line.slice(2)
        const inlineNodes = parseInline(itemText)
        result.push(
          <BulletItem key={`li-${i}`}>
            <BulletDot>{'\u2022'}</BulletDot>
            <Text style={{ flexDirection: 'row', flexWrap: 'wrap', flex: 1 }}>
              {renderInline(inlineNodes, `li-${i}`)}
            </Text>
          </BulletItem>
        )
        continue
      }

      // Regular line — parse inline
      const inlineNodes = parseInline(line)

      // Collect URLs for previews
      for (const node of inlineNodes) {
        if (node.type === 'link') {
          detectedUrls.push(node.url)
        }
      }

      if (i > 0 && !lines[i - 1].trimStart().startsWith('```')) {
        // Add line break between lines (preserve whitespace like pre-wrap)
      }

      result.push(
        <Text
          key={`line-${i}`}
          fontFamily="$fontMono"
          fontSize={14}
          lineHeight={22}
          color="$surfaceForeground"
          // @ts-expect-error -- web white-space
          style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', display: 'block' }}
        >
          {renderInline(inlineNodes, `line-${i}`)}
        </Text>
      )
    }

    // Handle unclosed code block
    if (inCodeBlock && codeBlockContent.length > 0) {
      const codeText = codeBlockContent.join('\n')
      result.push(
        <CodeBlock key={`cb-${blockIndex}`}>
          <CodeBlockHeader>
            {codeBlockLang && <LangLabel>{codeBlockLang}</LangLabel>}
            <CopyButton text={codeText} />
          </CodeBlockHeader>
          {codeBlockLang ? highlightCode(codeText, codeBlockLang) : <CodeBlockText>{codeText}</CodeBlockText>}
        </CodeBlock>
      )
    }

    return { elements: result, urls: detectedUrls }
  }, [content])

  return (
    <View>
      {elements}
      {urls.map((url) => (
        <LinkPreview key={url} url={url} />
      ))}
    </View>
  )
}
